import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import storyCommunity from "@/assets/story-community-support.webp";
import storyDisaster from "@/assets/story-disaster-relief.webp";
import storyEducation from "@/assets/story-education.webp";

export const FeaturedStories = () => {
  const stories = [
    {
      id: 1,
      slug: "supporting-local-communities-in-crisis",
      badge: "Community Impact",
      badgeVariant: "default" as const,
      title: "Supporting Local Communities in Crisis",
      link: "Learn more",
      image: storyCommunity,
      bgColor: "from-primary/10 to-primary/20",
    },
    {
      id: 2,
      slug: "emergency-relief-for-flood-victims",
      badge: "Natural Disaster",
      badgeVariant: "secondary" as const,
      title: "Emergency Relief for Flood Victims",
      link: "Learn more",
      image: storyDisaster,
      bgColor: "from-secondary/20 to-secondary/30",
    },
    {
      id: 3,
      slug: "education-for-underprivileged-children",
      badge: "Urgent Cause",
      badgeVariant: "outline" as const,
      title: "Education for Underprivileged Children",
      link: "Learn more",
      image: storyEducation,
      bgColor: "from-accent/20 to-accent/30",
    },
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-in space-y-4">
          <div className="inline-flex items-center gap-3 mx-auto">
            <span className="h-px w-8 bg-primary/60" />
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-primary">
              From the field
            </span>
            <span className="h-px w-8 bg-primary/60" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.05] text-foreground">
            Stories{" "}
            <span className="font-serif italic font-medium text-primary">behind</span>{" "}
            the donations.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            The people, the places, and the moments your support actually reached.
          </p>
        </div>

        {/* Stories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {stories.map((story, index) => (
            <Card
              key={story.id}
              className="group overflow-hidden border-none shadow-card hover:shadow-primary transition-all duration-300 animate-scale-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardContent className="p-0">
                {/* Story Image with Badge */}
                <div className={`relative bg-gradient-to-br ${story.bgColor} overflow-hidden`}>
                  <div className="absolute top-4 left-4 z-10">
                    <Badge variant={story.badgeVariant} className="shadow-sm">
                      {story.badge}
                    </Badge>
                  </div>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={story.image}
                      alt={story.title}
                      width="400"
                      height="300"
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                </div>

                {/* Story Content */}
                <div className="p-6 bg-card">
                  <h3 className="text-xl font-semibold text-foreground mb-4 group-hover:text-primary transition-colors">
                    {story.title}
                  </h3>
                  <Link
                    to={`/stories/${story.slug}`}
                    className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all duration-300"
                  >
                    {story.link}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
